// Central, testable alias table that normalizes organization types for the UI.
// Presentation/filtering only — never mutates stored values or API contracts.

export const ORG_TYPE_ALL = "__all__";

// Canonical families, in the stable display order requested by product.
export const ORG_TYPE_CATEGORIES = [
  { key: "destination", fr: "Destination / Office de tourisme", en: "Destination / Tourism board" },
  { key: "hotel", fr: "Hôtel / Lodge", en: "Hotel / Lodge" },
  { key: "hotel_group", fr: "Groupe ou collection hôtelière", en: "Hotel group or collection" },
  { key: "dmc", fr: "DMC", en: "DMC" },
  { key: "airline", fr: "Compagnie aérienne", en: "Airline" },
  { key: "transport", fr: "Transport", en: "Transport" },
  { key: "travel_agency", fr: "Agence de voyages / Tour-opérateur", en: "Travel agency / Tour operator" },
  { key: "activity", fr: "Activité touristique / Attraction", en: "Activity / Attraction" },
  { key: "airport", fr: "Aéroport", en: "Airport" },
  { key: "restaurant", fr: "Restaurant", en: "Restaurant" },
  { key: "retail", fr: "Commerce / Retail", en: "Retail" },
  { key: "other", fr: "Autre partenaire touristique", en: "Other tourism partner" },
];

// Raw variants (any case / spacing / underscore / synonym) mapped to a canonical key.
const ALIASES = {
  destination: [
    "DESTINATION", "TOURISM_OFFICE", "TOURISM_BOARD", "OFFICE_DE_TOURISME", "OFFICE_TOURISME",
    "DMO", "TOURISM", "TOURISM_AUTHORITY", "NTO", "CVB", "OT", "TOURISM_ORGANIZATION",
  ],
  hotel: [
    "HOTEL", "LODGE", "HOTEL_LODGE", "ACCOMMODATION", "HEBERGEMENT", "RESORT",
    "BOUTIQUE_HOTEL", "PROPERTY", "HOTEL_PROPERTY", "BB", "GUESTHOUSE",
  ],
  hotel_group: [
    "HOTEL_GROUP", "HOTEL_CHAIN", "HOTEL_COLLECTION", "COLLECTION", "CHAIN", "GROUP",
    "HOSPITALITY_GROUP", "BRAND", "HOTEL_BRAND",
  ],
  dmc: ["DMC", "DESTINATION_MANAGEMENT_COMPANY"],
  airline: ["AIRLINE", "CARRIER", "AIR_CARRIER", "COMPAGNIE_AERIENNE"],
  transport: [
    "TRANSPORT", "TRANSPORTATION", "TRANSPORT_COMPANY", "GROUND_TRANSPORT", "RAIL",
    "CRUISE", "FERRY", "BUS", "COACH", "MOBILITY",
  ],
  travel_agency: [
    "TRAVEL_AGENCY", "TOUR_OPERATOR", "TRAVEL_AGENT", "TA", "TO", "OTA", "AGENCY",
    "TOUR_OPERATOR_AGENCY", "AGENCE", "AGENCE_DE_VOYAGES", "TOUR_OPERATEUR",
  ],
  activity: [
    "ACTIVITY", "ATTRACTION", "EXPERIENCE", "TOUR", "MUSEUM", "THEME_PARK", "LEISURE",
    "TOURISM_ACTIVITY", "EXCURSION", "ACTIVITE",
  ],
  airport: ["AIRPORT", "AEROPORT"],
  restaurant: ["RESTAURANT", "F_AND_B", "FOOD_AND_BEVERAGE", "FB", "DINING", "GASTRONOMY", "FNB"],
  retail: ["RETAIL", "SHOP", "STORE", "COMMERCE", "BOUTIQUE", "DUTY_FREE"],
};

const normToken = (raw) =>
  String(raw ?? "")
    .trim()
    .toUpperCase()
    .replace(/[\s\-./]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");

const LOOKUP = (() => {
  const m = {};
  for (const [key, list] of Object.entries(ALIASES)) {
    for (const alias of list) m[normToken(alias)] = key;
  }
  return m;
})();

// Returns a canonical family key. Unknown values fall back to "other" (never lost).
export function canonicalizeOrgType(raw) {
  const tok = normToken(raw);
  if (!tok) return "other";
  return LOOKUP[tok] || "other";
}

// Human, localized label for a single org type (canonical key OR raw value).
export function orgTypeLabel(value, lang = "fr") {
  const key = ORG_TYPE_CATEGORIES.some((c) => c.key === value) ? value : canonicalizeOrgType(value);
  const cat = ORG_TYPE_CATEGORIES.find((c) => c.key === key) || ORG_TYPE_CATEGORIES.find((c) => c.key === "other");
  return cat[lang] || cat.fr;
}

// Build the deduplicated, ordered filter options for the families present in `rawTypes`.
// "All types" is always first. No raw technical labels are ever surfaced.
export function buildOrgTypeOptions(rawTypes = [], lang = "fr") {
  const present = new Set((rawTypes || []).map(canonicalizeOrgType));
  const families = ORG_TYPE_CATEGORIES.filter((c) => present.has(c.key)).map((c) => ({
    value: c.key,
    label: c[lang] || c.fr,
  }));
  return [{ value: ORG_TYPE_ALL, label: lang === "en" ? "All types" : "Tous les types" }, ...families];
}

// True if a raw org type belongs to the selected canonical family (or "all").
export function orgTypeMatches(rawType, selected) {
  if (!selected || selected === ORG_TYPE_ALL) return true;
  return canonicalizeOrgType(rawType) === selected;
}
